import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  DialogProps,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import TuneIcon from "@mui/icons-material/SettingsOutlined";
import LockIcon from "@mui/icons-material/Lock";
import styled from "@emotion/styled";
import { ConnectedApps } from "@features/ConnectedApps";
import { Settings, SecuritySettings } from "@features/Settings";
import { useState } from "react";
import { SettingsModalTab } from "@store/ui.store";

const SettingsSidebar = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1rem;
  gap: 0.5rem;
  background-color: ${(p) => p.theme.colors.background2};
  border-top-right-radius: 8px;

  ${(p) => p.theme.media.mobileDown} {
    border-radius: 0;
    padding: 0.75rem;
    flex-direction: row;
    gap: 0.5rem;
    justify-content: space-between;
    align-items: center;
  }
`;
const SettingsSidebarButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-align: left;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  background-color: ${(p) => p.theme.colors.background2};
  border: none;
  cursor: pointer;
  font-family: inherit;
  color: ${(p) => p.theme.button.text.colors.text};

  &[aria-selected="true"],
  &:hover {
    background-color: ${(p) => p.theme.colors.background3};
  }

  ${(p) => p.theme.media.mobileDown} {
    width: 100%;
  }
`;
const CustomDialogTitle = styled(DialogTitle)`
  display: grid;
  grid-template-columns: auto 40px;
  align-items: center;
  padding-right: 1rem;
`;
const CustomDialogContent = styled(DialogContent)`
  display: grid;
  padding: 0;
  grid-template-columns: 186px auto;
  min-height: 250px;

  h6 {
    font-size: ${(p) => p.theme.text.fontSizes.l};
  }

  ${(p) => p.theme.media.mobileDown} {
    margin-top: 0;
    row-gap: 1.5rem;
    grid-template-columns: auto;
    height: initial;
  }
`;
const SettingsContent = styled.div`
  margin-bottom: 2rem;
  padding: 1rem;

  ${(p) => p.theme.media.mobileDown} {
    padding: 0 1rem;
  }
`;

interface Props extends DialogProps {
  open: boolean;
  onClose: () => void;
  initialTab?: string | null;
  highlightAppId?: string;
}

export type SettingsModalSections = SettingsModalTab;

export const SettingsModal = (props: Props) => {
  const { open, onClose, initialTab, highlightAppId, ...rest } = props;
  const [activeSection, setActiveSection] = useState<SettingsModalSections>(
    (initialTab as SettingsModalSections) ?? "general",
  );

  const handleClickSidebarButton = (e: React.MouseEvent<HTMLButtonElement>) => {
    const value = e.currentTarget.value as SettingsModalSections;
    setActiveSection(value);
  };

  return (
    <Dialog {...rest} fullWidth maxWidth="sm" onClose={onClose} open={open}>
      <CustomDialogTitle id="settings-dialog-title">
        <span>Settings</span>
        <IconButton aria-label="close" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </CustomDialogTitle>
      <CustomDialogContent>
        <SettingsSidebar>
          <SettingsSidebarButton
            aria-selected={activeSection === "general"}
            value="general"
            onClick={handleClickSidebarButton}
          >
            <TuneIcon fontSize="small" /> General
          </SettingsSidebarButton>

          <SettingsSidebarButton
            aria-selected={activeSection === "integrations"}
            value="integrations"
            onClick={handleClickSidebarButton}
          >
            <HubOutlinedIcon fontSize="small" /> Integrations
          </SettingsSidebarButton>

          <SettingsSidebarButton
            aria-selected={activeSection === "security"}
            value="security"
            onClick={handleClickSidebarButton}
          >
            <LockIcon fontSize="small" /> Security
          </SettingsSidebarButton>
        </SettingsSidebar>
        <SettingsContent>
          {activeSection === "general" && <Settings size="small" />}
          {activeSection === "integrations" && (
            <ConnectedApps size="small" highlightAppId={highlightAppId} />
          )}
          {activeSection === "security" && <SecuritySettings />}
        </SettingsContent>
      </CustomDialogContent>
    </Dialog>
  );
};
